import React, { useState, useMemo } from 'react';
import Chart from 'react-apexcharts';
import { 
  Users, 
  TrendingUp, 
  Clock, 
  CheckCircle, 
  Target,
  Calendar,
  Download,
  Filter,
  RefreshCw,
  Activity,
  Award,
  BarChart3,
  PieChart,
  User,
  AlertTriangle,
  Zap
} from 'lucide-react';
import { UserAvatar } from '../UI/UserAvatar';

interface TeamAnalyticsDashboardProps {
  users: any[];
  issues: any[];
  projects: any[];
  currentUser: any;
}

export const TeamAnalyticsDashboard: React.FC<TeamAnalyticsDashboardProps> = ({
  users,
  issues,
  projects,
  currentUser
}) => {
  const [selectedTimeRange, setSelectedTimeRange] = useState('30d');
  const [selectedTeamMember, setSelectedTeamMember] = useState('all');
  const [selectedProject, setSelectedProject] = useState('all');
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Calculate team metrics
  const teamMetrics = useMemo(() => {
    const totalIssues = issues.length;
    const completedIssues = issues.filter(i => i.status === 'done').length;
    const inProgressIssues = issues.filter(i => i.status === 'in-progress').length;
    const overdueTasks = issues.filter(i => 
      i.dueDate && new Date(i.dueDate) < new Date() && i.status !== 'done'
    ).length;

    const teamProductivity = users.map(user => {
      const userIssues = issues.filter(i => i.assignee === user.id);
      const completedByUser = userIssues.filter(i => i.status === 'done').length;
      const totalAssigned = userIssues.length;
      
      return {
        ...user,
        assigned: totalAssigned,
        completed: completedByUser,
        completionRate: totalAssigned > 0 ? (completedByUser / totalAssigned) * 100 : 0,
        inProgress: userIssues.filter(i => i.status === 'in-progress').length,
        overdue: userIssues.filter(i => 
          i.dueDate && new Date(i.dueDate) < new Date() && i.status !== 'done'
        ).length
      };
    });

    return {
      totalIssues,
      completedIssues,
      inProgressIssues,
      overdueTasks,
      completionRate: totalIssues > 0 ? (completedIssues / totalIssues) * 100 : 0,
      teamProductivity
    };
  }, [users, issues]);

  // Chart configurations
  const productivityChartOptions = {
    chart: {
      type: 'bar' as const,
      height: 350,
      toolbar: { show: true },
      animations: { enabled: true }
    },
    colors: ['#10B981', '#3B82F6', '#EF4444'],
    xaxis: {
      categories: teamMetrics.teamProductivity.map(u => u.displayName.split(' ')[0]),
      title: { text: 'Team Members' }
    },
    yaxis: {
      title: { text: 'Number of Issues' }
    },
    legend: { position: 'top' as const },
    plotOptions: {
      bar: {
        borderRadius: 4,
        columnWidth: '60%'
      }
    },
    dataLabels: { enabled: true },
    responsive: [{
      breakpoint: 768,
      options: {
        chart: { height: 300 },
        plotOptions: {
          bar: { columnWidth: '80%' }
        }
      }
    }]
  };

  const productivityChartSeries = [
    {
      name: 'Completed',
      data: teamMetrics.teamProductivity.map(u => u.completed)
    },
    {
      name: 'In Progress',
      data: teamMetrics.teamProductivity.map(u => u.inProgress)
    },
    {
      name: 'Overdue',
      data: teamMetrics.teamProductivity.map(u => u.overdue)
    }
  ];

  const completionRateChartOptions = {
    chart: {
      type: 'donut' as const,
      height: 350
    },
    colors: ['#10B981', '#F59E0B', '#EF4444', '#6B7280'],
    labels: ['Completed', 'In Progress', 'Overdue', 'To Do'],
    legend: { position: 'bottom' as const },
    plotOptions: {
      pie: {
        donut: {
          size: '70%',
          labels: {
            show: true,
            total: {
              show: true,
              label: 'Total Issues',
              formatter: () => teamMetrics.totalIssues.toString()
            }
          }
        }
      }
    },
    dataLabels: {
      enabled: true,
      formatter: (val: number) => `${val.toFixed(1)}%`
    },
    responsive: [{
      breakpoint: 768,
      options: {
        chart: { height: 300 },
        legend: { position: 'bottom' as const }
      }
    }]
  };

  const completionRateChartSeries = [
    teamMetrics.completedIssues,
    teamMetrics.inProgressIssues,
    teamMetrics.overdueTasks,
    teamMetrics.totalIssues - teamMetrics.completedIssues - teamMetrics.inProgressIssues - teamMetrics.overdueTasks
  ];

  const velocityChartOptions = {
    chart: {
      type: 'line' as const,
      height: 350,
      toolbar: { show: true }
    },
    stroke: { curve: 'smooth' as const, width: 3 },
    colors: ['#8B5CF6', '#06B6D4'],
    xaxis: {
      categories: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
      title: { text: 'Time Period' }
    },
    yaxis: {
      title: { text: 'Issues Completed' }
    },
    legend: { position: 'top' as const },
    responsive: [{
      breakpoint: 768,
      options: {
        chart: { height: 300 }
      }
    }]
  };

  const velocityChartSeries = [
    {
      name: 'Team Velocity',
      data: [12, 15, 18, 22]
    },
    {
      name: 'Target',
      data: [15, 15, 15, 15]
    }
  ];

  const handleRefresh = async () => {
    setIsRefreshing(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsRefreshing(false);
  };

  const handleExport = () => {
    // Simulate export functionality
    const data = {
      metrics: teamMetrics,
      timeRange: selectedTimeRange,
      exportDate: new Date().toISOString()
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `team-analytics-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const timeRangeOptions = [
    { value: '7d', label: 'Last 7 days' },
    { value: '30d', label: 'Last 30 days' },
    { value: '90d', label: 'Last 3 months' },
    { value: '1y', label: 'Last year' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-pink-50">
      <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-6 md:space-y-8">
        {/* Hero Header */}
        <div className="relative overflow-hidden bg-gradient-to-r from-purple-600 via-pink-600 to-indigo-600 rounded-2xl shadow-xl">
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="relative p-6 md:p-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-white mb-1">
                      Team Analytics Dashboard
                    </h1>
                    <p className="text-purple-100">Monitor team performance and track productivity metrics</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4 text-purple-100">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    <span className="text-sm">Live Updates</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Activity className="w-4 h-4" />
                    <span className="text-sm">Real-time Analytics</span>
                  </div>
                </div>
              </div>

              {/* Controls */}
              <div className="flex flex-col lg:flex-row gap-3">
                <div className="flex flex-col">
                  <label className="text-xs font-semibold text-purple-100 uppercase tracking-wide mb-1 flex items-center">
                    <Calendar className="w-3 h-3 mr-1" />
                    Time Range
                  </label>
                  <select
                    value={selectedTimeRange}
                    onChange={(e) => setSelectedTimeRange(e.target.value)}
                    className="px-3 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg focus:ring-2 focus:ring-white/50 focus:border-white text-white text-sm min-w-[140px] placeholder-white/70"
                  >
                    {timeRangeOptions.map((option) => (
                      <option key={option.value} value={option.value} className="text-gray-900">
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex space-x-2 lg:items-end">
                  <button
                    onClick={handleRefresh}
                    disabled={isRefreshing}
                    className="flex items-center space-x-2 px-3 py-2 bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 text-white rounded-lg transition-all duration-200 text-sm disabled:opacity-50"
                  >
                    <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                    <span className="hidden sm:inline">Refresh</span>
                  </button>

                  <button
                    onClick={handleExport}
                    className="flex items-center space-x-2 px-3 py-2 bg-white/20 backdrop-blur-sm border border-white/30 hover:bg-white/30 text-white rounded-lg transition-all duration-200 text-sm"
                  >
                    <Download className="w-4 h-4" />
                    <span className="hidden sm:inline">Export</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Key Metrics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {[
            {
              title: 'Total Issues',
              value: teamMetrics.totalIssues,
              icon: Target,
              gradient: 'from-blue-500 to-blue-600',
              change: '+12%',
              changeType: 'positive'
            },
            {
              title: 'Completed',
              value: teamMetrics.completedIssues,
              icon: CheckCircle,
              gradient: 'from-emerald-500 to-emerald-600',
              change: '+23%',
              changeType: 'positive'
            },
            {
              title: 'In Progress',
              value: teamMetrics.inProgressIssues,
              icon: Clock,
              gradient: 'from-amber-500 to-amber-600',
              change: '+5%',
              changeType: 'positive'
            },
            {
              title: 'Overdue',
              value: teamMetrics.overdueTasks,
              icon: AlertTriangle,
              gradient: 'from-red-500 to-red-600',
              change: '-8%',
              changeType: 'negative'
            }
          ].map((metric, index) => {
            const Icon = metric.icon;
            return (
              <div key={index} className="group relative">
                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-white/10 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl border border-white/20 p-4 md:p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-xs md:text-sm font-medium text-gray-600 mb-1">{metric.title}</p>
                      <p className="text-xl md:text-3xl font-bold text-gray-900">{metric.value}</p>
                      <div className="flex items-center mt-2">
                        <span className={`text-xs md:text-sm font-medium ${
                          metric.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
                        } flex items-center`}>
                          {metric.changeType === 'positive' ? (
                            <TrendingUp className="w-3 h-3 mr-1" />
                          ) : (
                            <TrendingDown className="w-3 h-3 mr-1" />
                          )}
                          {metric.change}
                        </span>
                        <span className="text-xs md:text-sm text-gray-500 ml-1 hidden sm:inline">from last period</span>
                      </div>
                    </div>
                    <div className={`w-10 h-10 md:w-12 md:h-12 bg-gradient-to-r ${metric.gradient} rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                      <Icon className="w-5 h-5 md:w-6 md:h-6 text-white" />
                    </div>
                  </div>
                  <div className="mt-4 flex items-center">
                    <div className={`w-1 h-1 bg-gradient-to-r ${metric.gradient} rounded-full mr-2`}></div>
                    <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full bg-gradient-to-r ${metric.gradient} rounded-full transition-all duration-500`}
                        style={{ width: `${Math.min((metric.value / Math.max(...[teamMetrics.totalIssues, teamMetrics.completedIssues, teamMetrics.inProgressIssues, teamMetrics.overdueTasks].filter(v => v > 0))) * 100, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Enhanced Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Team Productivity Chart */}
          <div className="group relative">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl border border-white/20 p-4 md:p-6 shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Team Productivity</h3>
                    <p className="text-sm text-gray-500">Individual performance breakdown</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2 text-sm text-blue-600">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span className="font-medium">Performance Data</span>
                </div>
              </div>
              <Chart
                options={productivityChartOptions}
                series={productivityChartSeries}
                type="bar"
                height={350}
              />
            </div>
          </div>

          {/* Completion Rate Chart */}
          <div className="group relative">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl border border-white/20 p-4 md:p-6 shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
                    <PieChart className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Issue Distribution</h3>
                    <p className="text-sm text-gray-500">Work status across the team</p>
                  </div>
                </div>
              </div>
              <Chart
                options={completionRateChartOptions}
                series={completionRateChartSeries}
                type="donut"
                height={350}
              />
            </div>
          </div>
        </div>

        {/* Enhanced Team Velocity Chart */}
        <div className="group relative">
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl border border-white/20 p-4 md:p-6 shadow-lg hover:shadow-xl transition-all duration-300">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Team Velocity Trend</h3>
                  <p className="text-sm text-gray-500">Weekly completion rates over time</p>
                </div>
              </div>
              <div className="flex items-center space-x-2 text-sm text-emerald-600">
                <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                <span className="font-medium">Velocity Tracking</span>
              </div>
            </div>
            <Chart
              options={velocityChartOptions}
              series={velocityChartSeries}
              type="line"
              height={350}
            />
          </div>
        </div>

        {/* Enhanced Individual Performance Table */}
        <div className="group relative">
          <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/10 to-blue-500/10 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl border border-white/20 p-4 md:p-6 shadow-lg hover:shadow-xl transition-all duration-300">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Individual Performance</h3>
                  <p className="text-sm text-gray-500">Detailed team member analytics</p>
                </div>
              </div>
              <div className="flex items-center space-x-2 text-sm text-indigo-600">
                <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                <span className="font-medium">Team Insights</span>
              </div>
            </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-2 text-sm font-medium text-gray-600">Team Member</th>
                <th className="text-center py-3 px-2 text-sm font-medium text-gray-600">Assigned</th>
                <th className="text-center py-3 px-2 text-sm font-medium text-gray-600">Completed</th>
                <th className="text-center py-3 px-2 text-sm font-medium text-gray-600">In Progress</th>
                <th className="text-center py-3 px-2 text-sm font-medium text-gray-600">Completion Rate</th>
                <th className="text-center py-3 px-2 text-sm font-medium text-gray-600">Status</th>
              </tr>
            </thead>
            <tbody>
              {teamMetrics.teamProductivity.map((member, index) => (
                <tr key={member.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-4 px-2">
                    <div className="flex items-center space-x-3">
                      <UserAvatar
                        name={member.displayName}
                        src={member.avatarUrl}
                        className="w-8 h-8 border-2 border-gray-200"
                      />
                      <div>
                        <div className="font-medium text-gray-900 text-sm">{member.displayName}</div>
                        <div className="text-xs text-gray-500">{member.emailAddress}</div>
                      </div>
                    </div>
                  </td>
                  <td className="text-center py-4 px-2 text-sm text-gray-900">{member.assigned}</td>
                  <td className="text-center py-4 px-2 text-sm text-green-600 font-medium">{member.completed}</td>
                  <td className="text-center py-4 px-2 text-sm text-blue-600">{member.inProgress}</td>
                  <td className="text-center py-4 px-2">
                    <div className="flex items-center justify-center space-x-2">
                      <div className="w-16 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-green-500 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${member.completionRate}%` }}
                        ></div>
                      </div>
                      <span className="text-sm font-medium text-gray-900">
                        {member.completionRate.toFixed(0)}%
                      </span>
                    </div>
                  </td>
                  <td className="text-center py-4 px-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      member.completionRate >= 80 
                        ? 'bg-green-100 text-green-800' 
                        : member.completionRate >= 60 
                        ? 'bg-yellow-100 text-yellow-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {member.completionRate >= 80 ? 'Excellent' : member.completionRate >= 60 ? 'Good' : 'Needs Attention'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

        {/* Enhanced Recent Activity */}
        <div className="group relative">
          <div className="absolute inset-0 bg-gradient-to-r from-orange-500/10 to-red-500/10 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl border border-white/20 p-4 md:p-6 shadow-lg hover:shadow-xl transition-all duration-300">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl flex items-center justify-center">
                  <Activity className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Recent Team Activity</h3>
                  <p className="text-sm text-gray-500">Latest updates and progress</p>
                </div>
              </div>
              <div className="flex items-center space-x-2 text-sm text-orange-600">
                <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
                <span className="font-medium">Live Feed</span>
              </div>
            </div>
        
        <div className="space-y-4">
          {[
            {
              user: 'Sarah Chen',
              action: 'completed',
              target: 'ECOM-15',
              summary: 'Payment gateway integration',
              time: '2 hours ago',
              avatar: 'https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=50&h=50&dpr=2',
              type: 'completion'
            },
            {
              user: 'Marcus Johnson',
              action: 'started work on',
              target: 'MOB-8',
              summary: 'Mobile navigation redesign',
              time: '4 hours ago',
              avatar: 'https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg?auto=compress&cs=tinysrgb&w=50&h=50&dpr=2',
              type: 'progress'
            },
            {
              user: 'Elena Rodriguez',
              action: 'reviewed',
              target: 'DASH-3',
              summary: 'Analytics dashboard improvements',
              time: '1 day ago',
              avatar: 'https://images.pexels.com/photos/415829/pexels-photo-415829.jpeg?auto=compress&cs=tinysrgb&w=50&h=50&dpr=2',
              type: 'review'
            }
          ].map((activity, index) => (
            <div key={index} className="flex space-x-3 p-3 hover:bg-gray-50 rounded-lg transition-colors">
              <img
                src={activity.avatar}
                alt={activity.user}
                className="w-8 h-8 rounded-full border border-gray-200"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-900">
                  <span className="font-medium">{activity.user}</span> {activity.action}{' '}
                  <span className="font-medium text-purple-600">{activity.target}</span>
                </p>
                <p className="text-sm text-gray-600 truncate">{activity.summary}</p>
                <p className="text-xs text-gray-500 mt-1">{activity.time}</p>
              </div>
              <div className={`w-2 h-2 rounded-full mt-2 ${
                activity.type === 'completion' ? 'bg-green-500' :
                activity.type === 'progress' ? 'bg-blue-500' : 'bg-purple-500'
              }`}></div>
            </div>
          ))}
        </div>
      </div>
      </div>
      </div>
    </div>
    </div>
  );
};
